/**
 * DynamicListTable - Componente genérico para listas estilo Excel
 *
 * Características:
 * - Columnas dinámicas según config
 * - Inline editing
 * - Keyboard navigation (Enter→next, ↑↓, Escape)
 * - Auto-save on blur
 * - Estilo plano, sin cards, sin colores decorativos
 * - RowItem memoizado: solo re-renderiza filas con cambios reales
 * - Highlight amarillo transitorio en filas que cambian (flag _highlight)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

export type ColumnType = 'number' | 'text' | 'readonly';

export interface DynamicColumn {
  key: string;           // campo en los datos
  label: string;         // encabezado de columna
  type: ColumnType;      // tipo de input
  editable?: boolean;    // si es editable (default true para number/text)
  width?: string;        // ancho CSS opcional
}

export interface DynamicListTableProps {
  columns: DynamicColumn[];
  rows: any[];
  onSave: (rowId: number, field: string, value: any) => Promise<void>;
  idField?: string;      // campo que contiene el ID (default: 'id')
  rowIdentifier?: string; // campo para identificar fila visualmente (default: primera columna)
}

// --- RowItem: memoizado para evitar re-render de filas sin cambios ---
interface RowItemProps {
  row: any;
  columns: DynamicColumn[];
  idField: string;
  rowIndex: number;
  editingCell: string | null;
  tempValue: string;
  inputRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
  onCellClick: (rowId: any, field: string, value: any, col: DynamicColumn) => void;
  onSave: (rowId: any, field: string) => void;
  onKeyDown: (e: React.KeyboardEvent, rowId: any, field: string, rowIndex: number, colIndex: number) => void;
  onBlur: (rowId: any, field: string) => void;
  onTempChange: (v: string) => void;
}

const RowItem = React.memo(({
  row, columns, idField, rowIndex, editingCell, tempValue,
  inputRefs, onCellClick, onSave, onKeyDown, onBlur, onTempChange,
}: RowItemProps) => {
  const rowId = row[idField];
  return (
    <tr
      style={{
        borderBottom: '1px solid #ddd',
        backgroundColor: row._highlight ? '#fffbcc'
          : row._restringido ? '#fee2e2'
          : row._top ? '#fef9c3'
          : '#ffffff',
        opacity: row._optimistic ? 0.7 : 1,
        outline: row._optimistic ? '1px dashed #93c5fd' : 'none',
        transition: 'background-color 0.15s ease, opacity 0.2s ease',
      }}
    >
      {columns.map((col, colIndex) => {
        const cellKey = `${rowId}-${col.key}`;
        const value = row[col.key];
        const isEditing = editingCell === cellKey;
        const isEditable = col.type !== 'readonly' && col.editable !== false;
        return (
          <td key={col.key} style={{ borderRight: '1px solid #ddd', padding: 0 }}>
            {isEditing ? (
              <input
                ref={el => { inputRefs.current[cellKey] = el; }}
                type={col.type === 'number' ? 'number' : 'text'}
                value={tempValue}
                onChange={e => onTempChange(e.target.value)}
                onKeyDown={e => onKeyDown(e, rowId, col.key, rowIndex, colIndex)}
                onBlur={() => onBlur(rowId, col.key)}
                style={{
                  width: '100%', padding: '4px 8px', border: '2px solid #000',
                  outline: 'none', fontFamily: 'monospace', fontSize: '14px', backgroundColor: '#fff',
                }}
              />
            ) : (
              <div
                onClick={() => isEditable && onCellClick(rowId, col.key, value, col)}
                style={{
                  padding: '4px 8px',
                  cursor: isEditable ? 'pointer' : 'default',
                  backgroundColor: isEditable ? '#fff' : '#f5f5f5',
                  minHeight: '24px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {colIndex === 0 && row._restringido && (
                  <span style={{ color: '#dc2626', fontWeight: 'bold', marginRight: '3px', fontSize: '11px' }}>R</span>
                )}
                {colIndex === 0 && row._top && !row._restringido && (
                  <span style={{ color: '#d97706', fontWeight: 'bold', marginRight: '3px', fontSize: '11px' }}>★</span>
                )}
                {value !== null && value !== undefined ? String(value) : '—'}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
});

export const DynamicListTable = React.memo(({
  columns,
  rows,
  onSave,
  idField = 'id',
  rowIdentifier: _rowIdentifier,
}: DynamicListTableProps) => {
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Focus en input cuando empieza edición
  useEffect(() => {
    if (editingCell) {
      const input = inputRefs.current[editingCell];
      if (input) { input.focus(); input.select(); }
    }
  }, [editingCell]);

  const handleCellClick = useCallback((rowId: any, field: string, currentValue: any, column: DynamicColumn) => {
    if (column.type === 'readonly' || column.editable === false) return;
    setEditingCell(`${rowId}-${field}`);
    setTempValue(String(currentValue || ''));
  }, []);

  const handleTempChange = useCallback((v: string) => setTempValue(v), []);

  const internalHandleSave = useCallback(async (rowId: any, field: string) => {
    const column = columns.find(c => c.key === field);
    if (!column) return;
    let finalValue: any = tempValue;
    if (column.type === 'number') finalValue = parseFloat(tempValue) || 0;
    try {
      await onSave(rowId, field, finalValue);
      setEditingCell(null);
    } catch (error) {
      console.error('Error guardando:', error);
      alert('Error al guardar. Ver consola.');
    }
  }, [tempValue, columns, onSave]);

  const handleKeyDown = useCallback((
    e: React.KeyboardEvent, rowId: any, field: string, rowIndex: number, colIndex: number,
  ) => {
    const goToRow = (targetIndex: number) => {
      const targetRow = rows[targetIndex];
      const col = columns[colIndex];
      if (col.type !== 'readonly' && col.editable !== false) {
        handleCellClick(targetRow[idField], field, targetRow[field], col);
      }
    };
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      internalHandleSave(rowId, field);
      if (rowIndex < rows.length - 1) setTimeout(() => goToRow(rowIndex + 1), 50);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingCell(null);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      internalHandleSave(rowId, field);
      if (rowIndex > 0) setTimeout(() => goToRow(rowIndex - 1), 50);
    }
  }, [rows, columns, idField, internalHandleSave, handleCellClick]);

  const handleBlur = useCallback((rowId: any, field: string) => {
    internalHandleSave(rowId, field);
  }, [internalHandleSave]);

  return (
    <div style={{ border: '1px solid #ccc', backgroundColor: '#fff', overflow: 'auto', maxHeight: '70vh' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '14px' }}>
        <thead>
          <tr style={{ backgroundColor: '#e0e0e0', position: 'sticky', top: 0 }}>
            {columns.map(col => (
              <th
                key={col.key}
                style={{
                  padding: '8px', textAlign: 'left', fontWeight: 'bold',
                  borderBottom: '2px solid #000', borderRight: '1px solid #ccc', width: col.width,
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <RowItem
              key={String(row[idField] ?? row.numero ?? row.num1 ?? rowIndex)}
              row={row}
              columns={columns}
              idField={idField}
              rowIndex={rowIndex}
              editingCell={editingCell}
              tempValue={tempValue}
              inputRefs={inputRefs}
              onCellClick={handleCellClick}
              onSave={internalHandleSave}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onTempChange={handleTempChange}
            />
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', color: '#666', fontFamily: 'monospace' }}>
          No hay datos para mostrar
        </div>
      )}
    </div>
  );
});
